import { Link } from "react-router-dom";
import { ArrowRight, Clock, Check, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDeliveries } from "@/lib/queries";
import { weekLabel, formatDate, formatDateTime, PHASE_LABELS, PRODUCTION_STATUS_LABELS, INSTALL_STATUS_LABELS } from "@/lib/format";
import { followUp, followUpHref, isActionable, deliveryText, splitProjectName, weekText } from "@/lib/followup";
import { PanelFrame, PanelSection } from "@/components/layout/SidePanel";
import { CompanyMark } from "@/components/common/CompanyMark";
import { PhaseBadge } from "@/components/common/Badges";

/** Summary for a delivery id: prefer the one passed in, otherwise the cached register. */
export function useDeliverySummary(id, fallback) {
  const { data } = useDeliveries({});
  if (fallback && fallback.id === id) return fallback;
  return (data || []).find((s) => s.id === id) || fallback || null;
}

const specText = (s) => {
  if (s.pending_spec_code) return `Rev. ${s.pending_spec_code} under behandling · rev. ${s.current_spec_code || "–"} gjelder`;
  if (s.current_spec_code) return `Rev. ${s.current_spec_code} · frigitt`;
  return "Ingen godkjent spesifikasjon";
};

const Row = ({ k, v, testId }) => (
  <div className="grid grid-cols-[112px_1fr] gap-3 py-2" data-testid={testId}>
    <dt className="t-meta pt-px">{k}</dt>
    <dd className="text-[14.5px] leading-snug">{v}</dd>
  </div>
);

/**
 * Quick preview of one delivery: the current task, the customer's words when there is a message,
 * the basis, prerequisites and one primary action. Full work happens on the delivery page.
 */
export const DeliveryPreview = ({ summary: s, onClose }) => {
  if (!s) return null;
  const fu = followUp(s);
  const actionable = isActionable(fu);
  const { name, unit } = splitProjectName(s);
  const del = deliveryText(s.dates);
  const m = s.latest_unhandled_message;
  const inst = s.installation || {};
  const prod = s.production || {};
  const showPrereqs = ["bestilt", "klargjoring", "produksjon"].includes(s.phase);
  const missing = (s.prerequisites || []).filter((p) => !p.ok);

  return (
    <PanelFrame
      testId={`delivery-preview-${s.number}`}
      eyebrow={s.number}
      chip={<PhaseBadge phase={s.phase} />}
      title={name}
      meta={<span className="inline-flex min-w-0 max-w-full items-center gap-2"><CompanyMark name={s.customer_name} type={s.customer_type} logo={s.customer_logo} size="xs" /><span className="truncate">{[unit, s.customer_name, s.stair_summary].filter(Boolean).join(" · ")}</span></span>}
      onClose={onClose}
      footer={
        <>
          <Link to={`/leveranser/${s.id}`} className="action-link" data-testid="preview-open-delivery">Åpne leveranse <ArrowRight /></Link>
          {actionable && (
            <Button asChild data-testid="preview-primary-action">
              <Link to={followUpHref(s)}>{fu.verb} <ArrowRight /></Link>
            </Button>
          )}
        </>
      }
    >
      <PanelSection label={actionable ? "Aktuell oppgave" : "Status"} testId="preview-task">
        <p className={`text-[15px] leading-snug ${actionable ? "font-semibold" : ""}`}>{fu.title}</p>
        {!actionable && (
          <p className="wait-text mt-1"><Clock /> {fu.kind === "venter" ? "Venter på andre – ingen handling her nå" : "Ingen handling nødvendig nå"}</p>
        )}
        {m && (
          <figure className="mt-3 rounded-md border-l-2 py-1 pl-3" style={{ borderColor: "hsl(var(--border))" }} data-testid="preview-message">
            <blockquote className="text-[14.5px] leading-relaxed">«{m.excerpt}»</blockquote>
            <figcaption className="t-meta mt-1.5">{m.sender} · {formatDateTime(m.received_at)}{m.subject ? ` · ${m.subject}` : ""}</figcaption>
          </figure>
        )}
      </PanelSection>

      <PanelSection label="Grunnlag" testId="preview-basis">
        <dl className="divide-y border-y">
          {!["foresporsel", "tilbud"].includes(s.phase) && <Row k="Spesifikasjon" v={specText(s)} />}
          {prod.planned_week && <Row k="Produksjon" v={`${PRODUCTION_STATUS_LABELS[prod.status] || "Planlagt"} · ${weekLabel(prod.planned_week).toLowerCase()}`} />}
          {inst.planned_week && <Row k="Montering" v={[weekLabel(inst.planned_week), inst.planned_date ? formatDate(inst.planned_date) : null, inst.installer || "montør ikke satt", inst.status && !["planlagt", "ikke_planlagt"].includes(inst.status) ? INSTALL_STATUS_LABELS[inst.status]?.toLowerCase() : null].filter(Boolean).join(" · ")} />}
          <Row k="Levering" v={del.sub ? `${del.main} · ${del.sub}` : del.main} testId="preview-delivery" />
          {s.dates?.agreed_delivery_week && s.dates?.desired_week && s.dates.desired_week !== s.dates.agreed_delivery_week && <Row k="Kundens ønske" v={weekText(s.dates.desired_week)} />}
          {s.responsible && <Row k="Ansvarlig" v={s.responsible} />}
        </dl>
      </PanelSection>

      {showPrereqs && (
        <PanelSection label="Forutsetninger for produksjon" testId="preview-prereqs">
          <ul className="space-y-1.5">
            {(s.prerequisites || []).map((p) => (
              <li key={p.key} className="flex items-start gap-2.5 text-[14.5px] leading-snug">
                {p.ok ? <Check className="mt-[2px] h-4 w-4 shrink-0" style={{ color: "hsl(var(--state-released))" }} aria-label="På plass" /> : <Circle className="mt-[3px] h-3.5 w-3.5 shrink-0" style={{ color: "hsl(var(--state-waiting))" }} aria-label="Mangler" />}
                <span className={p.ok ? "text-muted-foreground" : "font-medium"}>{p.ok ? p.label : p.missing || p.label}</span>
              </li>
            ))}
          </ul>
          {missing.length === 0 && <p className="t-meta mt-2">Alt er på plass for produksjon.</p>}
        </PanelSection>
      )}

      {s.open_service_cases > 0 && (
        <PanelSection label="Service" testId="preview-service">
          <Link to={`/service?leveranse=${s.id}`} className="action-link">{s.open_service_cases} åpne serviceoppdrag <ArrowRight /></Link>
        </PanelSection>
      )}
    </PanelFrame>
  );
};
